import React, { useEffect, useState, useRef } from 'react';
import { SafeAreaView, View, FlatList, Alert } from 'react-native';
import { Text, Button, Divider, Icon, Overlay } from 'react-native-elements';
import { connect } from 'react-redux';
import { RootState } from '../redux';
import { AgentStackParamList } from '../AgentStack';
import { StackScreenProps, StackNavigationProp } from '@react-navigation/stack';
import Firebase from '../components/FirebaseContext';
import { Tools, ToolSubmission, ToolResponses, RenderDescription } from '../utils/types/Tools';
import Renderer from '../utils/Renderer';
import ScreenActivity from '../components/ScreenActivity';
import { verticalScale, scale } from 'react-native-size-matters';
import { BarCodeScanner } from 'expo-barcode-scanner';
import { colors } from '../utils/constants';
import * as ImagePicker from 'expo-image-picker';
import * as Location from 'expo-location';
import { FailureToast, SuccessToast } from '../utils';
import { User } from '../utils/types/Users';
import { RouteProp } from '@react-navigation/native';
import firebase from 'firebase';
import { Hospital } from '../utils/types/others';

type NavProp = StackScreenProps<AgentStackParamList, 'Tool'>;

interface ToolProps extends NavProp {
	user: User;
	route: RouteProp<AgentStackParamList, 'Tool'>;
	navigation: StackNavigationProp<AgentStackParamList, 'Tool'>;
}

function Tool({ user, route, navigation }: ToolProps) {
	const { docId, project } = route.params;
	const firestore = Firebase.firestore();
	const storage = Firebase.storage();
	const flRef = useRef<FlatList<RenderDescription>>(null);
	// permissions first
	const [askPermissions, setAskPermissions] = useState<number>();
	const [hasQRcodePermission, setHasQRcodePermission] = useState(false);
	const [lauchQRScanner, setLaunchQRScanner] = useState(false);
	const [QRdata, setQRdata] = useState('');
	const [hasCameraPermission, setHasCameraPermission] = useState(false);
	const [cameraData, setCameraData] = useState('');
	const [hasLocationPermission, setHasLocationPermission] = useState(false);
	useEffect(() => {
		(async () => {
			const { status } = await BarCodeScanner.requestPermissionsAsync();
			setHasQRcodePermission(status === 'granted');
		})();

		(async () => {
			const { status } = await ImagePicker.requestCameraPermissionsAsync();
			setHasCameraPermission(status === 'granted');
		})();

		(async () => {
			const { status } = await Location.requestPermissionsAsync();
			setHasLocationPermission(status === 'granted');
		})();
	}, [askPermissions]);

	const [tool, setTool] = useState<Tools>();
	const [failedFetch, setFailedFetch] = useState(false);
	const [loadingActivity, setLoadingActivity] = useState('');
	useEffect(() => {
		// provider injector
		function providerInjector(t: Tools): Tools {
			const provider: Array<RenderDescription> = [
				{
					content: 'Type of Provider',
					type: 'single-choice',
					required: true,
					options: ['Private', 'Public'],
					selections: [],
				},
			];
			firestore
				.collection('hospitals')
				.get()
				.then((doc) => {
					if (!doc.empty) {
						const hospitals: Array<string> = [];
						doc.forEach((doc) => {
							hospitals.push((doc.data() as Hospital).name);
						});
						// add hospital list to provider
						provider[1] = {
							content: 'Name of Hospital',
							type: 'list',
							required: true,
							options: hospitals,
							selections: [],
						};
						// inject to tool questions
						t.questions = [...provider, ...t.questions];
						//adjust begin numbering index if set
						if (t?.allowNumbering) t.beginNumberingAt += 2;
						setTool(t);
					}
				})
				.catch(() => {
					setFailedFetch(true);
				});
			return t;
		}

		if (docId) {
			// fetch tool
			firestore
				.collection('tools')
				.doc(docId)
				.get()
				.then((doc) => {
					if (doc.exists) {
						const t = doc.data() as Tools;
						// auto inject provider
						providerInjector(t);
					}
				})
				.catch(() => {
					setFailedFetch(true);
				});
		}
	}, [docId, firestore]);

	return (
		<SafeAreaView style={{ flex: 1, backgroundColor: '#fff' }}>
			{tool ? (
				<View style={{ paddingHorizontal: 20, flex: 1, paddingTop: 10 }}>
					<Text
						style={{
							fontWeight: '700',
							marginBottom: verticalScale(20),
							textAlign: 'center',
							fontSize: verticalScale(20),
						}}
					>
						{tool.name}
					</Text>
					<Divider />
					<FlatList
						ref={flRef}
						showsVerticalScrollIndicator={false}
						alwaysBounceVertical={true}
						style={{ marginVertical: 10 }}
						data={tool.questions}
						renderItem={({ item, index }) => (
							<Renderer
								question={item}
								index={index}
								allowNumbering={tool.allowNumbering || true}
								beginNumberingAt={tool.beginNumberingAt > 9 || tool.beginNumberingAt < 1 ? 1 : tool.beginNumberingAt}
							/>
						)}
						ListHeaderComponent={
							<View style={{ flexDirection: 'column', flex: 1 }}>
								<View style={{ flexDirection: 'row', flex: 1, justifyContent: 'center', paddingHorizontal: scale(20) }}>
									<Icon
										name="qrcode-scan"
										type="material-community"
										size={verticalScale(24)}
										containerStyle={{ marginHorizontal: 10 }}
										color={QRdata.length > 0 ? '#22b900' : '#000'}
										onPress={() => {
											if (!hasQRcodePermission) {
												// ask for permissions again
												setAskPermissions(Math.random());
												return;
											}
											setLaunchQRScanner(true);
										}}
									/>
									<Icon
										name="camera-plus"
										type="material-community"
										size={verticalScale(24)}
										containerStyle={{ marginHorizontal: 10 }}
										color={cameraData.length > 0 ? '#22b900' : '#000'}
										onPress={async () => {
											if (!hasCameraPermission) {
												// ask for permissions again
												setAskPermissions(Math.random());
											}
											const result = await ImagePicker.launchCameraAsync({ quality: 0.4 });
											if (!result.cancelled) {
												setCameraData(result.uri);
											}
										}}
									/>
								</View>
								{lauchQRScanner && hasQRcodePermission && (
									<Overlay isVisible={lauchQRScanner} overlayStyle={{ height: '100%', width: '100%' }}>
										<View style={{ flex: 1 }}>
											<BarCodeScanner
												onBarCodeScanned={
													QRdata.length < 0
														? () => {
																Alert.alert('Invalid QR Code');
																setLaunchQRScanner(false);
														  }
														: ({ data }) => {
																setQRdata(data);
																setLaunchQRScanner(false);
														  }
												}
												style={{ height: '90%', width: '100%', alignSelf: 'center' }}
											/>
											<Button
												buttonStyle={{ backgroundColor: colors.red }}
												title="Cancel"
												containerStyle={{ width: 200, alignSelf: 'center' }}
												onPress={() => setLaunchQRScanner(false)}
											/>
										</View>
									</Overlay>
								)}
							</View>
						}
						ListFooterComponent={
							<Button
								onPress={async () => {
									// validate all required fields
									const errors = tool.questions.filter((q) => q.required == true && q.selections.length == 0);
									if (errors.length > 0) {
										FailureToast(`Required field: ${errors[0].content}`);
										flRef.current?.scrollToIndex({
											animated: true,
											index: tool.questions.indexOf(errors[0]),
											viewOffset: 10,
										});
										return;
									}
									// requires location data to submit
									if (!hasLocationPermission) {
										// ask for permission again
										setAskPermissions(Math.random());
										FailureToast('To submit your response, enable location data');
										return;
									}
									const { coords } = await Location.getCurrentPositionAsync({ enableHighAccuracy: true, accuracy: 5 });
									const addr = await Location.reverseGeocodeAsync(coords);
									let downloadUrl = '';
									try {
										if (cameraData.length > 1) {
											//upload image first
											setLoadingActivity('Uploading and saving captured image...');
											const response = await fetch(cameraData);
											const blob = await response.blob();
											const storageRef = storage.ref().child(`${project}/${user.uid}/${Date.now()}.jpg`);
											await storageRef.put(blob);
											downloadUrl = await storageRef.getDownloadURL();
										}
										// provider type and hospital name are guaranteed to be index 0 and 1 repectively
										const [provider, hospital] = tool.questions;
										const toolSubmission: ToolSubmission = {
											tool: tool,
											date: firebase.firestore.FieldValue.serverTimestamp(),
											hmoId: user.hia || '',
											hiaId: user.uid,
											picture: downloadUrl,
											barcode: QRdata,
											location: {
												value: `${addr[0].name} ${addr[0].street}, ${addr[0].city} ${addr[0].region}`,
												geo: new firebase.firestore.GeoPoint(coords.latitude, coords.longitude),
											},
											providerType: provider.selections.join('') as 'Private' | 'Public',
											hospital: hospital.selections.join(''),
										};
										setLoadingActivity('Saving to database...');
										// save using firestore
										const submission = await firestore.collection('submittedTools').add(toolSubmission);
										// batch individual response to database
										const batch = firestore.batch();
										const collection = firestore.collection('toolResponses');
										tool.questions.forEach((r) => {
											const toolResponse: ToolResponses = {
												toolSubmissionId: submission.id,
												toolId: tool.id,
												projectId: project,
												hiaId: user.hia || '',
												providerType: provider.selections.join('') as 'Private' | 'Public',
												hospital: hospital.selections.join(''),
												question: r.content,
												answer: r.type == 'multi-choice' ? r.selections : r.selections.join(''),
											};
											batch.set(collection.doc(), toolResponse);
										});
										batch.commit();
										// Scroll top and navigate back
										flRef.current?.scrollToIndex({
											animated: true,
											index: 1,
											viewOffset: 10,
										});
										setLoadingActivity('');
										SuccessToast('Data capture successful');
										navigation.goBack();
									} catch (error) {
										FailureToast('Failed to save captured data');
										setLoadingActivity('');
									}
								}}
								title="Submit Response"
								buttonStyle={{ backgroundColor: '#275036', paddingVertical: 12 }}
								containerStyle={{ marginBottom: 40 }}
								loading={loadingActivity.length > 0}
							/>
						}
						keyExtractor={(item) => item.content.replace(/[^\w]/gi, '')}
					/>
				</View>
			) : failedFetch ? (
				<View style={{ paddingHorizontal: 20, flex: 1, justifyContent: 'center' }}>
					<Text style={{ textAlign: 'center' }} h4>
						We encountered a problem loading this tool. Please notify the Admin.
					</Text>
				</View>
			) : (
				<></>
			)}
			<ScreenActivity active={!tool && !failedFetch} msg="Fetching tool..." />
			<ScreenActivity active={loadingActivity.length > 0} msg={loadingActivity} />
		</SafeAreaView>
	);
}

const mapStateToProps = ({ user }: RootState) => {
	return {
		user,
	};
};

export default connect(mapStateToProps, null)(Tool);
